import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  Inject,
  OnDestroy,
  OnInit,
  ViewChild
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DIALOG_DATA, DialogModule, DialogRef } from '@angular/cdk/dialog';
import { Subject, takeUntil } from 'rxjs';
import { ProductsService } from '../products/products.service';
import { PackageLabelResponse } from './product-creation.types';

import { loadLabelCodeLibraries, packageQrImage, renderLabelBarcode } from './package-label-codes';

@Component({
  selector: 'app-product-package-label-dialog',
  standalone: true,
  imports: [CommonModule, FormsModule, DialogModule],
  templateUrl: './product-package-label-dialog.component.html',
  styleUrls: [
    './product-create-dialog.component.scss',
    './product-package-label-dialog.component.scss',
    '../../assets/print/product-package-label-layout.css'
  ]
})
export class ProductPackageLabelDialogComponent implements OnInit, OnDestroy {
  @ViewChild('printArea') printArea?: ElementRef<HTMLElement>;
  @ViewChild('modelBarcode') modelBarcode?: ElementRef<SVGElement>;
  @ViewChild('serialBarcode') serialBarcode?: ElementRef<SVGElement>;
  codesReady = false;
  label: PackageLabelResponse | null = null;
  loading = true;
  printing = false;
  error = '';
  qrImage = '';
  brand: 'FBI' | 'ULAAP' = 'FBI';
  private destroyed = new Subject<void>();
  private alive = true;

  constructor(
    @Inject(DIALOG_DATA) public data: { uuid: string; theme: 'dark' | 'light' },
    public readonly ref: DialogRef,
    private readonly service: ProductsService,
    private readonly changeDetector: ChangeDetectorRef
  ) {}

  get logo(): string {
    return this.brand === 'FBI'
      ? 'assets/fbi_logo_side_split_difference_small.png'
      : 'assets/ulaap_logo.png';
  }
  get model(): string {
    if (!this.label) {
      return '';
    }
    if (this.label.product_count === 1) {
      return this.label.contents[0]?.product_model_number || this.label.package.product_model || '';
    }
    return this.label.package.product_model || '';
  }
  get serial(): string {
    if (!this.label) {
      return '';
    }
    if (this.label.product_count === 1) {
      return (
        this.label.contents[0]?.product_serial_number ||
        this.label.package.pallet_serial_number ||
        ''
      );
    }
    return this.label.package.pallet_serial_number || '';
  }

  get description(): string {
    if (!this.label) {
      return '';
    }
    if (this.label.package.description) {
      return this.label.package.description;
    }
    if (this.label.product_count === 1) {
      return this.label.contents[0]?.description || '';
    }
    return '';
  }

  get quantity(): number | null {
    if (!this.label) {
      return null;
    }
    return this.label.product_count > 0
      ? this.label.product_count
      : this.label.package.product_count;
  }

  get labelDate(): string {
    const value = this.label?.package.intake_date || this.label?.package.created;
    return value ? value.replace('T', ' ').slice(0, 16) : '';
  }

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    this.loading = true;
    this.error = '';
    this.qrImage = '';
    this.codesReady = false;
    this.label = null;
    this.service
      .packageLabel(this.data.uuid)
      .pipe(takeUntil(this.destroyed))
      .subscribe({
        next: async (response) => {
          try {
            if (
              response.package.package_uuid !== this.data.uuid ||
              response.qr_value !== 'package,' + this.data.uuid
            ) {
              throw new Error('Invalid package label identity.');
            }
            this.label = response;
            const barcodeRenderer = await loadLabelCodeLibraries();
            const qrImage = await packageQrImage(response.qr_value);
            if (!this.alive) {
              return;
            }
            this.qrImage = qrImage;
            // The package data controls which SVG targets exist in the template.
            this.changeDetector.detectChanges();
            renderLabelBarcode(barcodeRenderer, this.modelBarcode?.nativeElement, this.model);
            renderLabelBarcode(barcodeRenderer, this.serialBarcode?.nativeElement, this.serial);
            this.codesReady = true;
          } catch (error) {
            this.error = error instanceof Error ? error.message : 'The label could not render.';
          } finally {
            this.loading = false;
          }
        },
        error: (error) => {
          this.loading = false;
          this.error = error.error?.message || 'The package label could not load.';
        }
      });
  }

  async print(): Promise<void> {
    if (!this.printArea || !this.codesReady || this.printing || this.loading || this.error) {
      return;
    }
    const page = window.open('', '_blank', 'width=500,height=740');
    if (!page) {
      this.error = 'Allow pop-ups for DAMS, then try Print again.';
      return;
    }
    this.printing = true;
    try {
      const style = page.document.createElement('link');
      style.rel = 'stylesheet';
      style.href = new URL('assets/print/product-package-label.css', document.baseURI).href;
      const stylesReady = new Promise<void>((resolve, reject) => {
        style.onload = () => resolve();
        style.onerror = () => reject(new Error('Print stylesheet could not load.'));
      });
      page.document.head.appendChild(style);
      const fontStyle = page.document.createElement('link');
      fontStyle.rel = 'stylesheet';
      fontStyle.href =
        'https://fonts.googleapis.com/css2?family=Roboto:wght@300;400;500&display=swap';
      const fontStyleReady = new Promise<void>((resolve) => {
        fontStyle.onload = () => resolve();
        fontStyle.onerror = () => resolve(); // Same sans-serif fallback as the app.
      });
      page.document.head.appendChild(fontStyle);
      page.document.title = 'Package label ' + this.data.uuid;
      const label = this.printArea.nativeElement.cloneNode(true) as HTMLElement;
      label.querySelectorAll('img').forEach((img) => {
        img.src = new URL(img.getAttribute('src') || '', document.baseURI).href;
      });
      page.document.body.appendChild(label);
      await Promise.all([
        stylesReady,
        fontStyleReady,
        ...Array.from(label.querySelectorAll('img')).map((img) => img.decode())
      ]);
      await page.document.fonts.ready;
      page.focus();
      page.print();
    } catch {
      this.error =
        'The print preview could not finish loading. Retry when all label images are visible.';
      page.close();
    } finally {
      this.printing = false;
    }
  }

  ngOnDestroy(): void {
    this.alive = false;
    this.destroyed.next();
    this.destroyed.complete();
  }
}
